import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
} from "@repo/ui/input-group";
import { cn } from "@repo/ui/lib/utils";
import { type RefObject, useCallback, useRef, useState } from "react";

import { activeThreadId$ } from "~/stores/threads";
import { ChatContextChips, useHasChatContextChips } from "./chat-context-chips";
import { ChatImagePreview } from "./chat-image-preview";
import {
  ChatInputEditor,
  type ChatInputEditorHandle,
} from "./chat-input-editor";
import { ChatModeSelector } from "./chat-mode-selector";
import { MAX_IMAGES_PER_MESSAGE } from "./constants";
import type { PendingImage } from "./types";

export type ChatInputProps = {
  /** Current send-side enablement signal — true when text or mentions exist. */
  hasContent: boolean;
  onChange: (state: { text: string; mentionCount: number }) => void;
  onSend: () => void;
  onStop: () => void;
  onAttachClick: () => void;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasteFiles: (files: File[]) => void;
  pendingImages: PendingImage[];
  onRemoveImage: (id: string) => void;
  isBusy: boolean;
  isProcessingImages: boolean;
  editorRef: RefObject<ChatInputEditorHandle | null>;
  fileInputRef: RefObject<HTMLInputElement | null>;
};

export const ChatInput = ({
  hasContent,
  onChange,
  onSend,
  onStop,
  onAttachClick,
  onFileInputChange,
  onPasteFiles,
  pendingImages,
  onRemoveImage,
  isBusy,
  isProcessingImages,
  editorRef,
  fileInputRef,
}: ChatInputProps) => {
  const threadId = useValue(activeThreadId$);
  const hasContextChips = useHasChatContextChips();
  // Keep the editor editable while the assistant is streaming so the user can
  // compose a follow-up. Send is gated separately by `isBusy` on the button
  // below, so this can't double-submit.
  const editorDisabled = !threadId;

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Drag events fire for every descendant, so a naive enter/leave toggle
  // flickers as the cursor crosses child boundaries. Track depth instead and
  // only clear when the counter returns to zero.
  const dragDepthRef = useRef(0);

  // Only react to actual file drags — ignore in-page text/HTML drags so we
  // don't show the dropzone overlay for unrelated drags.
  const isFileDrag = (e: React.DragEvent) =>
    e.dataTransfer.types.includes("Files");

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      // The editor's handleDrop (chat-input-editor.tsx) already forwarded
      // these files and called preventDefault; without this guard the wrapper
      // would add the same files a second time.
      if (e.defaultPrevented) {
        dragDepthRef.current = 0;
        setIsDraggingOver(false);
        return;
      }
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDraggingOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) onPasteFiles(files);
    },
    [onPasteFiles],
  );

  return (
    <>
      <InputGroup
        className={cn(
          "max-h-48 transition-colors",
          isDraggingOver && "ring-2 ring-primary/40",
        )}
        onClick={(e) => {
          // The editor is a contenteditable div, not an input/textarea, so
          // InputGroupAddon's auto-focus selector won't reach it. Explicitly
          // forward clicks on the wrapper to the editor.
          if (e.target === e.currentTarget) {
            editorRef.current?.focus();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {hasContextChips && (
          <InputGroupAddon align="block-start" className="pb-0">
            <ChatContextChips />
          </InputGroupAddon>
        )}
        {pendingImages.length > 0 && (
          <InputGroupAddon align="block-start" className="pb-0">
            <div className="no-scrollbar flex gap-1.5 overflow-x-auto">
              {pendingImages.map((img) => (
                <ChatImagePreview
                  key={img.id}
                  image={img}
                  onRemove={() => onRemoveImage(img.id)}
                />
              ))}
            </div>
          </InputGroupAddon>
        )}
        <ChatInputEditor
          ref={editorRef}
          placeholder="Ask Mora..."
          disabled={editorDisabled}
          onChange={onChange}
          onSubmit={onSend}
          onPasteFiles={onPasteFiles}
        />
        <InputGroupAddon align="block-end" className="pt-0">
          <div className="flex w-full items-center">
            <ChatModeSelector />
            <div className="ml-auto flex items-center gap-0.5">
              <InputGroupButton
                size="icon-xs"
                variant="ghost"
                onClick={onAttachClick}
                disabled={
                  isBusy || pendingImages.length >= MAX_IMAGES_PER_MESSAGE
                }
                aria-label="Attach image"
              >
                <Icon name="image" />
              </InputGroupButton>
              {isBusy ? (
                <InputGroupButton
                  variant="secondary"
                  size="icon-xs"
                  onClick={onStop}
                  className="rounded-full"
                  aria-label="Stop"
                >
                  <Icon name="stop" />
                </InputGroupButton>
              ) : (
                <InputGroupButton
                  variant="secondary"
                  size="icon-xs"
                  onClick={onSend}
                  disabled={
                    !hasContent || isBusy || isProcessingImages || !threadId
                  }
                  className="rounded-full"
                >
                  <Icon name="arrow-up" />
                </InputGroupButton>
              )}
            </div>
          </div>
        </InputGroupAddon>
      </InputGroup>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onFileInputChange}
      />
    </>
  );
};
